<template>
  <div :class="$style.page">
    <BaseContainer :class="$style.container">
      <form @submit.prevent="submit">
        <BaseLoader
          :class="$style.box"
          :is-active="isFormLoading"
        >
          <FormBox
            :heading="formStepData.heading"
            :lead="formStepData.lead"
          >
            <template v-if="formStep === 1">
              <BaseField
                is-required
                :error="givenNameError"
                :class="$style.field"
              >
                <template #label>
                  GIVEN NAME
                </template>

                <BaseInput
                  v-model="givenName"
                  :has-error="Boolean(givenNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="familyNameError"
                :class="$style.field"
              >
                <template #label>
                  FAMILY NAME
                </template>

                <BaseInput
                  v-model="familyName"
                  :has-error="Boolean(familyNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :class="$style.field"
                :error="timeZoneError"
              >
                <template #label>
                  TIME ZONE
                </template>

                <BaseSelect
                  v-model="timeZone"
                  :class="$style.multiselect"
                  :has-error="Boolean(timeZoneError)"
                >
                  <option disabled>
                    Select
                  </option>
                  <option
                    v-for="option in $options.TIME_ZONES_OPTIONS"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </BaseSelect>
              </BaseField>

              <template v-if="companyId">
                <FormBoxDivider />

                <div>
                  <h4 :class="$style.billingInformationHeading">
                    Billing information
                  </h4>
                  <p :class="$style.billingInformationContent">
                    {{ isAdmin ? `This user’s` : 'Your' }} company
                    <template v-if="invoicePaidUntil">
                      is paid until {{ format(new Date(invoicePaidUntil), 'MMM d y') }}.
                    </template>
                    <template v-else>
                      doesn't have paid invoices.
                    </template>
                    <br>
                    <router-link
                      v-if="isAdmin"
                      v-slot="{ href, navigate }"
                      :to="{ name: 'EditCompany', params: { companyId: companyId } }"
                      custom
                    >
                      <BaseLink
                        :href="href"
                        @click="navigate"
                      >
                        Manage billing info here.
                      </BaseLink>
                    </router-link>
                  </p>
                </div>
              </template>
            </template>

            <template #footer>
              <BaseButton
                variant="success"
                type="submit"
                is-block
              >
                {{ formStepData.submitText }}
              </BaseButton>
            </template>
          </FormBox>
        </BaseLoader>
      </form>
    </BaseContainer>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapGetters, mapState } from 'vuex';
import { format } from 'date-fns';
import BaseContainer from '@/components/base/Container';
import BaseField from '@/components/base/Field';
import BaseInput from '@/components/base/Input';
import BaseButton from '@/components/base/Button';
import BaseLink from '@/components/base/Link';
import BaseSelect from '@/components/base/Select';
import BaseLoader from '@/components/base/Loader';
import FormBox, { FormBoxDivider } from '@/components/FormBox';
import { adminQueries, companyAdminQueries } from '@/aws-api';
import { timeZones } from '@/config';

export default {
  components: {
    BaseContainer,
    BaseField,
    BaseInput,
    BaseButton,
    BaseLink,
    BaseSelect,
    BaseLoader,
    FormBox,
    FormBoxDivider,
  },
  TIME_ZONES_OPTIONS: timeZones,
  FORM_STEPS_DATA: [
    {
      heading: 'Edit user',
      submitText: 'Save',
    },
    {
      heading: 'User saved',
      submitText: 'Go to users',
    },
  ],
  data() {
    return {
      givenName: '',
      givenNameError: null,
      familyName: '',
      familyNameError: null,
      timeZone: null,
      timeZoneError: null,
      isFormLoading: false,
      companyId: null,
      invoicePaidUntil: null,
      formStep: 1,
    };
  },
  computed: {
    ...mapGetters('user', ['isAdmin']),
    ...mapState('user', ['user']),
    formStepData() {
      return this.$options.FORM_STEPS_DATA[this.formStep - 1];
    },
  },
  mounted() {
    this.getUserData();
  },
  methods: {
    format,
    async getUserData() {
      this.isFormLoading = true;
      const api = this.isAdmin ? adminQueries : companyAdminQueries;

      const data = await Promise.all([
        api.getUser(this.$route.params.userId),
        this.$apollo.query({
          query: gql`query ($userId: String!) {
            users_by_pk(id: $userId) {
              time_zone
              company {
                id
                invoices(limit: 1, order_by: {paid_until: desc}) {
                  paid_until
                }
              }
            }
          }`,
          variables: {
            userId: this.$route.params.userId,
          },
        }),
      ]);

      this.givenName = data[0].UserAttributes.find((x) => x.Name === 'given_name').Value;
      this.familyName = data[0].UserAttributes.find((x) => x.Name === 'family_name').Value;
      const company = data[1].data.users_by_pk?.company;
      this.companyId = company?.id;
      this.invoicePaidUntil = company?.invoices?.[0]?.paid_until;
      this.timeZone = data[1].data.users_by_pk.time_zone;

      this.isFormLoading = false;
    },
    async saveUser() {
      this.givenNameError = null;
      this.familyNameError = null;
      this.timeZoneError = null;
      let hasError = false;

      if (this.givenName.trim() === '') {
        hasError = true;
        this.givenNameError = 'Can\'t be empty';
      }

      if (this.familyName.trim() === '') {
        hasError = true;
        this.familyNameError = 'Can\'t be empty';
      }

      if (this.timeZone == null) {
        hasError = true;
        this.timeZoneError = 'Can\'t be empty';
      }

      if (hasError) {
        return;
      }

      this.isFormLoading = true;

      try {
        const api = this.isAdmin ? adminQueries : companyAdminQueries;

        await Promise.all([
          api.updateUserAttributes(this.$route.params.userId, {
            given_name: this.givenName,
            family_name: this.familyName,
          }),
          this.$apollo.mutate({
            mutation: gql`mutation ($userId: String!, $timeZone: String!) {
              update_users_by_pk(pk_columns: {id: $userId}, _set: {time_zone: $timeZone}) {
                id
              }
            }`,
            variables: {
              userId: this.$route.params.userId,
              timeZone: this.timeZone,
            },
          }),
        ]);

        this.formStep = 2;
        this.isFormLoading = false;
      } catch (e) {
        console.log(e); // eslint-disable-line no-console
      }
    },
    submit() {
      switch (this.formStep) {
        case 1:
          return this.saveUser();
        case 2:
          return this.$router.push({ name: 'Users' });
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
  background: #fafafa;
}

.container {
  display: flex;
  justify-content: center;
}

.box {
  width: 470px;
}

.field {

  &:not(:first-child) {
    margin-top: 20px;
  }
}

.multiselect {
  width: 100%;
}

.billingInformationHeading {
  padding: 0;
  margin: 0;
  font-size: 16px;
  color: $title;
}

.billingInformationContent {
  margin: 5px 0 0;
  font-size: 16px;
  line-height: 1.375;
  color: $title;
}
</style>
